/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  GuestChargingProperties,
  GuestChargingRequest,
  GuestChargingResponse,
  GuestChargingInitialProperties,
} from '../../interface/guest-charging-schedule.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { changeDateFormat } from '../../shared-functions/DateTime';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import device from '../../utils/api/device';

// a thunk to fetch users
export const fetchGuestCharging = createAsyncThunk<GuestChargingResponse, GuestChargingRequest>(
  'guest-charging/fetchGuestCharging',
  async (data: GuestChargingRequest, { dispatch }) => {
    try {
      const response = await device.fetchGuestCharging(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

// a thunk to fetch devices filters
export const fetchListGuestChargingFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('guest-charging/fetchListGuestChargingFilters', async (_, { dispatch }) => {
  try {
    const response = await device.fetchListGuestChargingFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const deviceFilter: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: GuestChargingInitialProperties = {
  schedules: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: deviceFilter,
  filterListLoading: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const guestChargingSlice = createSlice({
  name: 'guest-charging',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateGuestChargingFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter } = UpdateCommonFilter(state.filters, action.payload);
        state.filters = updatedCommonFilter;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
      }
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchGuestCharging.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchGuestCharging.fulfilled, (state, action) => {
        state.loading = false;
        state.schedules = action.payload?.results?.length
          ? action.payload.results?.map((stationData: GuestChargingProperties) => ({
              ...stationData,
              id: stationData._id,
              chargingStationNames:
                stationData?.chargingStationData
                  ?.map((chargingStation) => chargingStation.name)
                  ?.join(', ') || '',
              time: stationData.timeSlots.map(
                (value) =>
                  `${FormatUtcString(ConvertUTCtoHHMM(value.startTime))} - ${FormatUtcString(ConvertUTCtoHHMM(value.endTime))}`,
              ),
              onSelectiveDates: stationData.onSelectiveDates.map((onSelectiveDate) =>
                changeDateFormat(ConvertUTCtoDate(onSelectiveDate)),
              ),
              fromDate: stationData?.fromDate ? ConvertUTCtoDate(stationData?.fromDate) : '',
              toDate: stationData?.toDate ? ConvertUTCtoDate(stationData?.toDate) : '',
            }))
          : [];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchGuestCharging.rejected, (state) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchListGuestChargingFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListGuestChargingFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListGuestChargingFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const { updateGuestChargingFilters, setSortData } = guestChargingSlice.actions;
// Export the reducer
export default guestChargingSlice.reducer;
